import { EffectComposer } from 'effect-composer';
import { RenderPass } from 'render-pass';
import { UnrealBloomPass } from 'unreal-bloom-pass';
import { ShaderPass } from 'shader-pass';
import { AfterimagePass } from 'afterimage-pass';
import { FXAAShader } from 'fxaa-shader';
import { HorizontalBlurShader } from 'horizontal-blur-shader';
import { VerticalBlurShader } from 'vertical-blur-shader';
import { OutputPass } from 'output-pass';
import * as THREE from 'three';

export class Renderer {
    constructor(renderer, scene, camera, width, height) {
        this.width = width;
        this.height = height;
        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        this.composer = null;
        this.usePostprocess = false;
    this.bloomPass = null;
    this.passes = [];


    }

    enablePostprocess(passesConfig) {
        // console.log('🌟 Activation du postprocess');

        // Reset previous state
        if (this.composer) {
            this.composer.dispose();
            this.composer = null;
        }
        this.bloomPass = null;
        this.passes = [];
    this.fxaaPass = null;
    this.afterimagePass = null;
    this.anamorphic = null; // { hPass, vPass, hStrength, vStrength }
    this.hasOutputPass = false;

        this.usePostprocess = true;

        // Hide the raw video canvas
        const videoCanvas = document.getElementById('videoCanvas');
        if (videoCanvas) {
            videoCanvas.style.display = 'none';
        }

        // Initialize EffectComposer for postprocessing
        this.composer = new EffectComposer(this.renderer);
        this.composer.addPass(new RenderPass(this.scene, this.camera));

        // Backward compatibility: if a plain object is passed, treat as bloom settings
        if (!Array.isArray(passesConfig)) {
            const opts = passesConfig || {};
            const bloom = new UnrealBloomPass(
                new THREE.Vector2(this.width, this.height),
                opts.bloomStrength ?? opts.strength ?? 0.5,
                opts.bloomRadius ?? opts.radius ?? 0.03,
                opts.bloomThreshold ?? opts.threshold ?? 1.5
            );
            this.bloomPass = bloom;
            this.composer.addPass(bloom);
            this.passes.push(bloom);
            return;
        }

    // New API: array of passes { type, options }
    this.hasOutputPass = false;
        passesConfig.forEach((passDef) => {
            if (!passDef || !passDef.type) {
                console.warn('Pass de post-process invalide:', passDef);
                return;
            }
            const type = passDef.type;
            const options = passDef.options || {};

            switch (type) {
                case 'UnrealBloomPass': {
                    const bloom = new UnrealBloomPass(
                        new THREE.Vector2(this.width, this.height),
                        options.strength ?? 0.5,
                        options.radius ?? 0.03,
                        options.threshold ?? 1.5
                    );
                    this.bloomPass = bloom; // keep reference for live updates
                    this.composer.addPass(bloom);
                    this.passes.push(bloom);
                    break;
                }
                case 'FXAA': {
                    const fxaa = new ShaderPass(FXAAShader);
                    // Set initial resolution (inverse)
                    fxaa.material.uniforms['resolution'].value.set(1 / this.width, 1 / this.height);
                    this.fxaaPass = fxaa;
                    this.composer.addPass(fxaa);
                    this.passes.push(fxaa);
                    break;
                }
                case 'AfterimagePass': {
                    const after = new AfterimagePass();
                    if (options.damp !== undefined) {
                        after.uniforms['damp'].value = options.damp;
                    }
                    this.afterimagePass = after;
                    this.composer.addPass(after);
                    this.passes.push(after);
                    break;
                }
                case 'AnamorphicPass': {
                    // Implemented via directional blur shaders
                    const hPass = new ShaderPass(HorizontalBlurShader);
                    const vPass = new ShaderPass(VerticalBlurShader);
                    const hStrength = (options.horizontalStrength !== undefined)
                        ? options.horizontalStrength
                        : (options.direction === 'horizontal' ? (options.amount ?? 2.0) : 0.0);
                    const vStrength = (options.verticalStrength !== undefined)
                        ? options.verticalStrength
                        : (options.direction === 'vertical' ? (options.amount ?? 0.5) : 0.0);

                    hPass.uniforms['h'].value = (hStrength || 0) / this.width;
                    vPass.uniforms['v'].value = (vStrength || 0) / this.height;

                    this.anamorphic = { hPass, vPass, hStrength: hStrength || 0, vStrength: vStrength || 0 };
                    this.composer.addPass(hPass);
                    this.composer.addPass(vPass);
                    this.passes.push(hPass, vPass);
                    break;
                }
                default:
                    console.warn(`Type de post-process non supporté: ${type}`);
                    break;
            }
        });

        // Always ensure an OutputPass at the end for correct color/encoding, unless explicitly provided
        if (!this.hasOutputPass) {
            const output = new OutputPass();
            this.composer.addPass(output);
            this.passes.push(output);
            this.hasOutputPass = true;
        }
    }

    updateBloomSettings(bloomSettings) {
        if (this.bloomPass) {
            const strength = bloomSettings.bloomStrength ?? bloomSettings.strength;
            const radius = bloomSettings.bloomRadius ?? bloomSettings.radius;
            const threshold = bloomSettings.bloomThreshold ?? bloomSettings.threshold;
            if (strength !== undefined) this.bloomPass.strength = strength;
            if (radius !== undefined) this.bloomPass.radius = radius;
            if (threshold !== undefined) this.bloomPass.threshold = threshold;
            if (this.composer) this.composer.render();
        }
    }

    disablePostprocess() {
        //console.log('🔄 Désactivation du postprocess');
        this.usePostprocess = false;
        
        // Nettoyer le composer et ses passes
        if (this.composer) {
            this.composer.dispose();
            this.composer = null;
        }
        this.bloomPass = null;
    this.fxaaPass = null;
    this.afterimagePass = null;
    this.anamorphic = null;

        // S'assurer que le canvas vidéo est visible
        const videoCanvas = document.getElementById('videoCanvas');
        if (videoCanvas) {
            videoCanvas.style.display = 'block';
        }
        
        // Nettoyage agressif du renderer WebGL
        this.renderer.clear(true, true, true); // Clear color, depth, stencil
        this.renderer.resetState(); // Reset tous les états WebGL
        
        // Forcer un rendu direct pour nettoyer l'état
        this.renderer.render(this.scene, this.camera);
    }

    dispose() {
        // console.log('🧹 Nettoyage du Renderer');
        
        // Nettoyer le composer
        if (this.composer) {
            this.composer.dispose();
            this.composer = null;
        }
        
        // Nettoyer les passes
        this.bloomPass = null;
        this.usePostprocess = false;
        
        // Nettoyage agressif du renderer WebGL
        if (this.renderer) {
            this.renderer.clear(true, true, true);
            this.renderer.resetState();
            
            // Nettoyer le cache des textures
            this.renderer.info.memory.textures = 0;
            this.renderer.info.memory.geometries = 0;
        }
    }

    adjustCanvas(width, height) {


        if (this.usePostprocess && this.composer) {

            this.composer.setSize(width, height, false);
        }

        this.renderer.setSize(width, height, false);

        // Update resolution-dependent uniforms
        if (this.fxaaPass) {
            this.fxaaPass.material.uniforms['resolution'].value.set(1 / width, 1 / height);
        }
        if (this.anamorphic) {
            if (this.anamorphic.hPass) {
                this.anamorphic.hPass.uniforms['h'].value = (this.anamorphic.hStrength || 0) / width;
            }
            if (this.anamorphic.vPass) {
                this.anamorphic.vPass.uniforms['v'].value = (this.anamorphic.vStrength || 0) / height;
            }
        }
    }

    render() {

        if (this.usePostprocess && this.composer) {
       
            this.composer.render();
            
        } else {
     
            this.renderer.render(this.scene, this.camera);
        }    }
}